// Browser-storage keys and helpers shared by the login flow, the routes and the user store.

// Available to every logged-in user, so it is the landing page after login
export const HOME_PATH = "/support_calls";

export const LOGIN_FORM_STORAGE_KEY = "coralis_office-user";
// Current session (user details + tokens); shared by all open tabs of the portal
export const SESSION_STORAGE_KEY = "coralis-support-user";
// Written on an explicit logout so other open tabs log out too (the value is just a timestamp)
export const LOGOUT_BROADCAST_KEY = "coralis-logout-at";
const USER_STORAGE_KEYS = ["coralis-support-user", "coralis-storeuser"];
const PASSWORD_FIELDS = ["pwd", "newPwd", "WEBPASSWORD"];

const REDIRECT_PATH_KEY = "redirectAfterLogin";
// Last page the user was on, kept across tabs and browser restarts: { username, path }
const LAST_PAGE_KEY = "coralis-last-page";
const REDIRECT_PARAMS_KEY = "redirectAfterLoginParams";
const LOGGED_OUT_KEY = "loggedOut";

const readJson = (storage, key) => {
  try {
    const value = storage.getItem(key);
    return value !== null ? JSON.parse(value) : null;
  } catch (error) {
    console.error(`Failed to read "${key}" from storage`, error);
    return null;
  }
};

const writeJson = (storage, key, value) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to write "${key}" to storage`, error);
  }
};

// Older versions saved the login password in plain text; strip it from anything already saved.
export const removeStoredPasswords = () => {
  [LOGIN_FORM_STORAGE_KEY, ...USER_STORAGE_KEYS].forEach((key) => {
    const data = readJson(window.localStorage, key);
    const userDetails = data?.userDetails;
    if (!userDetails || !PASSWORD_FIELDS.some((field) => field in userDetails)) return;

    const cleanDetails = { ...userDetails };
    PASSWORD_FIELDS.forEach((field) => delete cleanDetails[field]);
    writeJson(window.localStorage, key, { ...data, userDetails: cleanDetails });
  });
};

export const getSavedLoginEmail = () =>
  readJson(window.localStorage, LOGIN_FORM_STORAGE_KEY)?.userDetails?.email || "";

export const saveLoginEmail = (email) => {
  writeJson(window.localStorage, LOGIN_FORM_STORAGE_KEY, { userDetails: { email } });
};

export const saveRedirectAfterLogin = (pathname, search) => {
  if (window.sessionStorage.getItem(LOGGED_OUT_KEY)) return;
  window.sessionStorage.setItem(REDIRECT_PATH_KEY, pathname);
  window.sessionStorage.setItem(REDIRECT_PARAMS_KEY, search);
};

// Returns the page the user was sent away from (if any) and forgets it, so it is used only once.
export const takeRedirectAfterLogin = () => {
  const pathname = window.sessionStorage.getItem(REDIRECT_PATH_KEY);
  const search = window.sessionStorage.getItem(REDIRECT_PARAMS_KEY) || "";
  clearRedirectAfterLogin();
  return pathname ? pathname + search : null;
};

export const clearRedirectAfterLogin = () => {
  window.sessionStorage.removeItem(REDIRECT_PATH_KEY);
  window.sessionStorage.removeItem(REDIRECT_PARAMS_KEY);
};

// Set on an explicit logout so the page the user left is not reopened on the next login
export const markLoggedOut = () => {
  window.sessionStorage.setItem(LOGGED_OUT_KEY, "1");
  clearRedirectAfterLogin();
};

export const clearLoggedOut = () => {
  window.sessionStorage.removeItem(LOGGED_OUT_KEY);
};

// Tells the other open tabs that the user logged out (they listen for this key's storage event)
export const broadcastLogout = () => {
  try {
    window.localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
  } catch (error) {
    console.error("Failed to broadcast logout to other tabs", error);
  }
};

// Remembers the page the logged-in user is on, twice: per tab (sessionStorage, so every open tab
// returns to its own page after logging in again) and for the browser (localStorage, used by a new
// tab or after the browser was closed). Neither is cleared on logout.
export const saveLastPage = (username, path) => {
  if (!username || !path) return;
  const lastPage = { username: username.toLowerCase(), path };
  writeJson(window.sessionStorage, LAST_PAGE_KEY, lastPage);
  writeJson(window.localStorage, LAST_PAGE_KEY, lastPage);
};

// This tab's own last page first, else the browser's. Only for the same user - another user
// logging in on this browser starts at home.
export const getLastPage = (username) => {
  if (!username) return null;
  const normalizedUsername = username.toLowerCase();
  for (const storage of [window.sessionStorage, window.localStorage]) {
    const lastPage = readJson(storage, LAST_PAGE_KEY);
    if (lastPage?.username === normalizedUsername && lastPage.path) return lastPage.path;
  }
  return null;
};

// Table sort the user chose ({ key, direction }), remembered per list (e.g. "tasks-all")
const SORT_STORAGE_PREFIX = "coralis-sort-";

export const getSavedSort = (listKey) => {
  if (!listKey) return null;
  const saved = readJson(window.localStorage, SORT_STORAGE_PREFIX + listKey);
  return saved?.key && saved?.direction ? saved : null;
};

export const saveSort = (listKey, sortConfig) => {
  if (!listKey || !sortConfig?.key) return;
  writeJson(window.localStorage, SORT_STORAGE_PREFIX + listKey, {
    key: sortConfig.key,
    direction: sortConfig.direction,
  });
};
